import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import Slider from '@/components/atoms/Slider';

import { useDistributionTimer } from '../hooks/useDistributionTimer';

import styles from './WeightDistributionPlayControl.module.scss';

export interface WeightDistributionPlayControlProps {
    /** Index of the day currently on the chart. */
    currentIndex: number;
    /** Number of days available to scrub through. */
    dayCount: number;
    onIndexChange: (index: number) => void;
}

/** Play button and scrubber that step the histogram through its days. */
export default function WeightDistributionPlayControl(props: WeightDistributionPlayControlProps) {
    const { currentIndex, dayCount, onIndexChange } = props;
    const [isPlaying, setIsPlaying] = useState(false);
    const { t } = useTranslation();

    // Nothing to step through with a single day.
    const disabled = dayCount < 2;
    const label =
        !disabled && isPlaying
            ? t('cageWeight.distribution.stop')
            : t('cageWeight.distribution.play');

    const { togglePlay } = useDistributionTimer({
        isPlaying,
        currentIndex,
        totalLength: dayCount,
        onIndexChange,
        onPlayingChange: setIsPlaying,
        disabled,
    });

    return (
        <div className={styles.weightDistributionPlayControl}>
            <Button
                className={styles.playButton}
                trackingId="weight_distribution_play"
                onClick={togglePlay}
                disabled={disabled}>
                {label}
            </Button>
            <Slider
                disabled={disabled}
                onChange={onIndexChange}
                value={currentIndex}
                min={0}
                max={Math.max(dayCount - 1, 0)}
                aria-label={t('cageWeight.weightDistribution.dayScrubber')}
            />
        </div>
    );
}
